import { cn } from "@/lib/utils";
import { Badge } from "@/components/ui/badge";
import { Loader2 } from "lucide-react";

export function PageHeader({ title, subtitle, action }) {
  return (
    <div className="flex flex-wrap items-start justify-between gap-4 mb-7">
      <div>
        <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight text-slate-900">{title}</h1>
        {subtitle && <p className="text-sm text-slate-500 mt-1.5 max-w-2xl">{subtitle}</p>}
      </div>
      {action}
    </div>
  );
}

export function StatCard({ label, value, hint, icon: Icon, accent = "primary", testid }) {
  const accents = {
    primary: "bg-emerald-50 text-emerald-700",
    blue: "bg-blue-50 text-blue-700",
    amber: "bg-amber-50 text-amber-700",
    violet: "bg-violet-50 text-violet-700",
    rose: "bg-rose-50 text-rose-700",
  };
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5 card-hover" data-testid={testid}>
      <div className="flex items-center justify-between">
        <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">{label}</span>
        {Icon && (
          <span className={cn("h-9 w-9 rounded-xl flex items-center justify-center", accents[accent])}>
            <Icon className="h-[18px] w-[18px]" />
          </span>
        )}
      </div>
      <div className="mt-3 text-2xl font-extrabold font-display text-slate-900">{value}</div>
      {hint && <div className="text-xs text-slate-500 mt-1">{hint}</div>}
    </div>
  );
}

export function Section8Badge({ section8 }) {
  return section8 ? (
    <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100 border-0 font-semibold">Section 8</Badge>
  ) : (
    <Badge className="bg-slate-100 text-slate-700 hover:bg-slate-100 border-0 font-semibold">Standard</Badge>
  );
}

export function StatusPill({ status }) {
  const map = {
    paid: "bg-emerald-100 text-emerald-800",
    confirmed: "bg-emerald-100 text-emerald-800",
    completed: "bg-emerald-100 text-emerald-800",
    partial: "bg-amber-100 text-amber-800",
    pending: "bg-amber-100 text-amber-800",
    in_progress: "bg-blue-100 text-blue-800",
    unpaid: "bg-rose-100 text-rose-800",
    open: "bg-rose-100 text-rose-800",
    due: "bg-amber-100 text-amber-800",
    past_due: "bg-rose-100 text-rose-800",
    active: "bg-emerald-100 text-emerald-800",
    canceled: "bg-slate-100 text-slate-600",
    trial: "bg-indigo-100 text-indigo-800",
    ending_soon: "bg-amber-100 text-amber-800",
    expired: "bg-rose-100 text-rose-800",
    suspended: "bg-slate-200 text-slate-700",
  };
  const label = { in_progress: "In Progress", past_due: "Past due", trial: "Free trial", ending_soon: "Ending soon" }[status] || status?.charAt(0).toUpperCase() + status?.slice(1);
  return (
    <span className={cn("inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold", map[status] || "bg-slate-100 text-slate-700")}>
      {label}
    </span>
  );
}

export function Loading({ label = "Loading…" }) {
  return (
    <div className="flex items-center justify-center py-24 text-slate-400 gap-2" data-testid="loading">
      <Loader2 className="h-5 w-5 animate-spin" /> {label}
    </div>
  );
}

export function EmptyState({ icon: Icon, title, subtitle, action, testid }) {
  return (
    <div className="rounded-2xl border border-dashed border-slate-300 bg-white py-16 px-6 text-center" data-testid={testid}>
      {Icon && (
        <div className="mx-auto h-12 w-12 rounded-2xl bg-slate-100 flex items-center justify-center mb-4">
          <Icon className="h-6 w-6 text-slate-400" />
        </div>
      )}
      <h3 className="font-display font-bold text-slate-800">{title}</h3>
      {subtitle && <p className="text-sm text-slate-500 mt-1.5 max-w-sm mx-auto">{subtitle}</p>}
      {action && <div className="mt-5">{action}</div>}
    </div>
  );
}
